/**
 * Template Registry — lookup templates by ID, get templates for a folder
 */
import type { TemplateDefinition, SportFolder } from '@graphicsim/shared-types';
import { templates } from '../data/templateData';
import { sportFolders } from '../data/sportFolders';

class TemplateRegistry {
  private templateMap = new Map<string, TemplateDefinition>();
  private folderMap = new Map<string, SportFolder>();

  constructor() {
    for (const t of templates) {
      this.templateMap.set(t.id, t);
    }
    for (const f of sportFolders) {
      this.folderMap.set(f.id, f);
    }
  }

  getTemplate(id: string): TemplateDefinition | undefined {
    return this.templateMap.get(id);
  }

  getFolder(id: string): SportFolder | undefined {
    return this.folderMap.get(id);
  }

  getFolders(): SportFolder[] {
    return sportFolders;
  }

  getTemplatesForFolder(folderId: string): TemplateDefinition[] {
    const folder = this.folderMap.get(folderId);
    if (!folder) return [];
    return folder.templateIds
      .map((tid) => this.templateMap.get(tid))
      .filter((t): t is TemplateDefinition => t !== undefined);
  }

  getAllTemplates(): TemplateDefinition[] {
    return templates;
  }

  /** Get default field values for a template */
  getDefaultFieldValues(templateId: string): Record<string, string> {
    const t = this.templateMap.get(templateId);
    if (!t) return {};
    const values: Record<string, string> = {};
    for (const field of t.fields) {
      values[field.id] = field.defaultValue;
    }
    return values;
  }
}

export const templateRegistry = new TemplateRegistry();
